import {useEffect, useCallback} from "react";
import {useQuery} from "@tanstack/react-query";
import {setShortJwt} from "../api";
import {USER_ROLES} from "../data/user";
import {flushPendingAnalytics} from "../libs/analytics";

/**
 * React hook that retrieves the authenticated user and their access level (role).
 *
 * Fetches user identity and role from GET /api/auth/me (reads the JWT cookie
 * server-side). Then issues a short-lived JWT via POST /auth/issue for
 * cross-origin API calls.
 *
 * Both fetches go through React Query so every useAuth consumer in the tree
 * shares one in-flight request and one cached result. Without this the page
 * fires /api/auth/me 12× and /auth/issue 8× because ~8 components call the
 * hook (AppRoot, Toolbar, StagingNotice, Admin, FlagsPage, etc.).
 *
 * Example:
 *   const { user, role, isLoading, shortJwt, refetchToken } = useAuth();
 */

/** The flag keys buildFeatureFlags() in src/server/lib/config.ts serves. Used
 * only for the missing-key WARNING — never to reject a payload. */
export const EXPECTED_FEATURE_FLAG_KEYS = ["videoExport", "bgDerivation", "bgListEditing", "livestream"];

/**
 * One shared empty set. `featureFlags` is a hook dependency (useChannelFromParams
 * memoizes on it), so a new {} per render invalidated it on every render and
 * looped App's editChannel effect on pages without flags.
 */
const NO_FEATURE_FLAGS = Object.freeze({});

/**
 * The featureFlags object to install from an /api/auth/me payload: the
 * server's object when it is one, else {}. Deliberately tolerant of missing
 * keys — consumers check `=== true`, so an absent flag reads as off, which is
 * exactly right for a response from an older server build.
 */
export function getInstallableFeatureFlags(me) {
  const flags = me?.featureFlags;
  return flags && typeof flags === "object" && !Array.isArray(flags) ? flags : NO_FEATURE_FLAGS;
}

/** Expected keys the payload lacks — [] when there is no payload yet. */
export function missingFeatureFlagKeys(me) {
  if (!me?.featureFlags || typeof me.featureFlags !== "object") return [];
  return EXPECTED_FEATURE_FLAG_KEYS.filter((key) => me.featureFlags[key] === undefined);
}

async function fetchMe() {
  const resp = await fetch("/api/auth/me", {
    credentials: "include",
    headers: {Accept: "application/json"},
  });
  // 401 isn't an error — unauthenticated page loads render unauth'd UI and
  // still consume env-level featureFlags from the body. Return the parsed
  // payload annotated with whether the request was ok.
  const json = await resp.json().catch(() => ({}));
  return {ok: resp.ok, ...json};
}

async function issueShortToken() {
  const resp = await fetch("/auth/issue", {
    method: "POST",
    credentials: "include",
    headers: {Accept: "application/json"},
  });
  if (resp.ok) {
    const json = await resp.json();
    if (!json.token) throw new Error("/auth/issue response missing token field");
    return json;
  }
  if (resp.status === 401) {
    // Cookie invalid mid-session — bounce to login. /auth/login not "/"
    // because "/" re-serves session-cached HTML and loops /auth/issue.
    // replace() so the failed page isn't in history. Throw so React Query
    // records an error state; otherwise {token:null} would cache as success
    // under staleTime:Infinity and lock the hook if the redirect is ever
    // suppressed (test runner, race).
    console.warn("refreshShortToken: 401 — cookie invalid, redirecting to login");
    window.location.replace("/auth/login");
    throw new Error("auth: session expired, redirecting to login");
  }
  // Transient non-401 (5xx, network): match prior behavior — log and return
  // {token:null}. api.js's independent 401-retry path will still try /auth/issue
  // again on the next cross-origin API call.
  console.warn(`[useAuth] /auth/issue: ${resp.status} — degraded mode, no short token`);
  return {token: null};
}

export function useAuth() {
  const {
    data: me,
    isPending: isMePending,
    isError: isMeError,
  } = useQuery({
    queryKey: ["auth-me"],
    queryFn: fetchMe,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const user = me?.user ?? null;

  // Install whatever flags the server sent. Every consumer checks a flag with
  // `=== true`, so an absent key already means "off" — which is the correct
  // reading of a payload from an older server that predates the key. An
  // all-or-nothing validity check here once collapsed EVERY flag to off for
  // the whole session whenever a rolling deploy served /api/auth/me from an
  // old pod missing the newest key (staleTime: Infinity keeps the response
  // for the session), so: never reject the payload over a missing key.
  const featureFlags = getInstallableFeatureFlags(me);

  useEffect(() => {
    const missing = missingFeatureFlagKeys(me);
    if (missing.length > 0) {
      console.warn(
        `[useAuth] /api/auth/me featureFlags is missing [${missing.join(", ")}] — ` +
          "an older server build? The missing flags read as OFF for this session.",
        me.featureFlags
      );
    }
    // Both helpers derive synchronously from me, so [me] is sufficient.
  }, [me]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    data: issueData,
    isPending: isIssuePending,
    refetch: refetchIssue,
  } = useQuery({
    queryKey: ["auth-issue"],
    queryFn: issueShortToken,
    enabled: !!user,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Mirror the token into api.js's module-level storage so request() can
  // attach it as a Bearer header on cross-origin calls. Always mirror once
  // the query has settled — including clearing to null on failure — so hook
  // state and api.js stay in sync. The `issueData !== undefined` guard
  // avoids a wipe-on-mount before the query has fired (the query is gated
  // by `enabled: !!user`, so issueData is undefined during initial load).
  useEffect(() => {
    if (issueData !== undefined) {
      setShortJwt(issueData?.token ?? null);
    }
  }, [issueData]);

  // Drain queued analytics events once auth has resolved in any of:
  //   - /auth/issue produced data (authenticated path)
  //   - /api/auth/me returned with no user (unauthenticated path)
  //   - /api/auth/me errored outright (network failure — retry:false so it
  //     won't self-recover; flush so we don't leak events for the session)
  // flushPendingAnalytics is idempotent.
  //
  // Must remain AFTER the setShortJwt effect above — React runs effects in
  // definition order, and flushPendingAnalytics → send() reads getShortJwt()
  // synchronously. Reordering would flush events before the token is stored,
  // firing the very 401s this queue was meant to prevent.
  useEffect(() => {
    const authResolved = issueData !== undefined || (me !== undefined && !me.user) || isMeError;
    if (authResolved) flushPendingAnalytics();
  }, [issueData, me, isMeError]);

  // Named refetchToken (not refetch) because it only re-fires /auth/issue.
  // It does not re-fetch /api/auth/me — user identity and featureFlags stay
  // cached for the session. No consumers currently call it.
  const refetchToken = useCallback(async () => {
    const {data} = await refetchIssue();
    return data?.token ?? null;
  }, [refetchIssue]);

  return {
    user,
    // /auth/issue payload wins over /api/auth/me for role — matches prior
    // behavior where setRole(json.role) ran after the issue call.
    role: issueData?.role || me?.role || USER_ROLES.EXTERNAL,
    // The real role. It differs from `role` only while a superadmin views the
    // app as another role (src/server/lib/view-as.ts). An older server sends
    // no realRole; then it is the same as `role`.
    realRole: me?.realRole || me?.role || USER_ROLES.EXTERNAL,
    featureFlags,
    shortJwt: issueData?.token ?? null,
    // Covers both queries. For unauthenticated users the issue query is
    // gated (enabled: !!user) so isIssuePending effectively drops out and
    // isLoading resolves to false once /api/auth/me returns 401.
    isLoading: isMePending || (!!user && isIssuePending),
    refetchToken,
  };
}
